import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { expenseService, groupService } from '../../services/api';
import UserSelect from '../../components/UserSelect';

/**
 * Página para adicionar nova despesa ao grupo.
 */
const AddExpensePage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    paidByMemberId: '',
    participantIds: [],
    expenseDate: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const data = await groupService.getMembers(groupId);
      setMembers(data);
      // Seleciona todos os participantes por padrão
      setFormData(prev => ({
        ...prev,
        participantIds: data.map(m => m.userId)
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleParticipantToggle = (memberId) => {
    const participants = [...formData.participantIds];
    const index = participants.indexOf(memberId);
    
    if (index > -1) {
      participants.splice(index, 1);
    } else {
      participants.push(memberId);
    }
    
    setFormData({
      ...formData,
      participantIds: participants,
    });
  };

  const selectAllParticipants = () => {
    setFormData({
      ...formData,
      participantIds: members.map(m => m.userId)
    });
  };

  const clearAllParticipants = () => {
    setFormData({
      ...formData,
      participantIds: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.participantIds.length === 0) {
      setError('Selecione pelo menos um participante');
      return;
    }
    
    setLoading(true);

    try {
      await expenseService.create({
        ...formData,
        groupId: parseInt(groupId),
        amount: parseFloat(formData.amount),
        paidByMemberId: parseInt(formData.paidByMemberId),
      });
      navigate(`/groups/${groupId}`);
    } catch (err) {
      setError('Erro ao criar despesa. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const splitAmount = formData.amount && formData.participantIds.length > 0
    ? (parseFloat(formData.amount.toString().replace(',', '.')) / formData.participantIds.length)
    : 0;

  console.log('Debug Split Amount:', {
    amount: formData.amount,
    participantCount: formData.participantIds.length,
    splitAmount: splitAmount,
    formatted: formatCurrency(splitAmount)
  });

  return (
    <div className="page-container">
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <Link 
            to={`/groups/${groupId}`}
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 8, 
              color: 'var(--text-secondary)',
              marginBottom: 16,
              fontSize: 14
            }}
          >
            <i className="fa-solid fa-arrow-left"></i> Voltar para o grupo
          </Link>
          
          <div className="page-header" style={{ marginBottom: 0 }}>
            <h1 className="page-title">
              <i className="fa-duotone fa-credit-card page-title-icon"></i>
              Nova Despesa
            </h1>
          </div>
        </div>

        {/* Form Card */}
        <div className="card-modern animate-fadeIn">
          <form onSubmit={handleSubmit} style={{ padding: 24 }}>
            {error && (
              <div className="error-message" style={{ marginBottom: 20 }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }}></i>
                {error}
              </div>
            )}

            {/* Descrição */}
            <div className="form-group">
              <label className="form-label">Descrição *</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-input"
                placeholder="Ex: Almoço, Cinema, Uber..."
                required
              />
            </div>

            {/* Valor */}
            <div className="form-group">
              <label className="form-label">Valor *</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-secondary)',
                  fontWeight: 500
                }}>R$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  className="form-input"
                  style={{ paddingLeft: 48 }}
                  placeholder="0,00"
                  required
                />
              </div>
            </div>

            {/* Data */}
            <div className="form-group">
              <label className="form-label">Data</label>
              <input
                type="date"
                name="expenseDate"
                value={formData.expenseDate}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Quem pagou */}
            <div className="form-group">
              <label className="form-label">Quem pagou? *</label>
              <UserSelect
                users={members.map(m => ({
                  id: m.userId,
                  name: m.userName,
                  nickname: m.nickname
                }))}
                value={formData.paidByMemberId}
                onChange={(userId) => setFormData(prev => ({ ...prev, paidByMemberId: userId }))}
                placeholder="Selecione quem pagou..."
                required
                noOptionsMessage="Nenhum membro encontrado"
              />
            </div>

            {/* Participantes */}
            <div className="form-group">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 12
              }}>
                <label className="form-label" style={{ marginBottom: 0 }}>
                  Participantes * ({formData.participantIds.length}/{members.length})
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button 
                    type="button"
                    onClick={selectAllParticipants}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary-color)',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 500
                    }}
                  >
                    Todos
                  </button>
                  <span style={{ color: 'var(--text-muted)' }}>|</span>
                  <button 
                    type="button"
                    onClick={clearAllParticipants}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    Limpar
                  </button>
                </div>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 8
              }}>
                {members.map((member) => {
                  const isSelected = formData.participantIds.includes(member.userId);
                  return (
                    <label
                      key={member.userId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '12px 14px',
                        borderRadius: 10,
                        border: `2px solid ${isSelected ? 'var(--primary-color)' : 'var(--border-color)'}`,
                        background: isSelected ? 'rgba(102, 126, 234, 0.08)' : 'var(--background-color)',
                        cursor: 'pointer',
                        transition: 'var(--transition-fast)'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleParticipantToggle(member.userId)}
                        style={{ 
                          width: 18, 
                          height: 18,
                          accentColor: 'var(--primary-color)'
                        }}
                      />
                      <span style={{ 
                        fontSize: 14, 
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? 'var(--primary-color)' : 'var(--text-primary)'
                      }}>
                        {member.nickname || member.userName}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Split Preview */}
            {formData.amount && formData.participantIds.length > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                borderRadius: 12,
                padding: 16,
                marginBottom: 24,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Cada participante pagará
                </div>
                <div style={{ 
                  fontSize: 24, 
                  fontWeight: 700, 
                  color: 'var(--primary-color)',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {formatCurrency(splitAmount)}
                </div>
              </div>
            )}

            {/* Observações */}
            <div className="form-group">
              <label className="form-label">Observações (opcional)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="form-input"
                rows={3}
                placeholder="Adicione detalhes sobre a despesa..."
                style={{ resize: 'vertical' }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                type="button"
                onClick={() => navigate(`/groups/${groupId}`)}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
                style={{ flex: 2 }}
              >
                {loading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-floppy-disk"></i>
                    Salvar Despesa
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddExpensePage;
